import {Alert} from 'src/design/atoms/Alert';
import {Center} from 'src/design/atoms/Center';
import {Loader} from 'src/design/atoms/Loader';
import {Text} from 'src/design/atoms/Text';
import React, {useEffect, useState} from 'react';
import {UserValidationStatusEnum} from 'src/api/ProactApiClient';
import {useAppDispatch} from 'src/redux/hooks';
import {useLazyCheckUserStatusQuery} from 'src/redux/queries/user';
import {setUserAccountStatus, setUsername} from 'src/redux/slices/authSlice';

export interface InvitationUserCheckProps {
  username: string;
  token: string;
  onUserValidated: (canProceed: boolean) => void;
}

export const InvitationUserCheck: React.FC<InvitationUserCheckProps> = ({
  username,
  onUserValidated,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const dispatch = useAppDispatch();
  const [checkUserStatus] = useLazyCheckUserStatusQuery();

  useEffect(() => {
    const run = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await checkUserStatus({email: username}).unwrap();

        dispatch(setUserAccountStatus(response.status));
        dispatch(setUsername(username));

        // Check if user is unconfirmed and can proceed with invitation
        const canProceed =
          response.status ===
          UserValidationStatusEnum.OAuthForcedChangePassword;

        onUserValidated(canProceed);
      } catch (error) {
        console.error('User status check error:', error);
        setError(
          'An error occurred while verifying your account status. Please check your connection and try again.'
        );
      } finally {
        setIsLoading(false);
      }
    };

    void run();
  }, [username, dispatch, onUserValidated]);

  if (isLoading) {
    return (
      <Center>
        <Loader size="sm" mr="md" />
        <Text>Checking account status...</Text>
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Error">
        {error}
      </Alert>
    );
  }

  return null;
};
